/* eslint-disable react/jsx-pascal-case */
import { GridFooter } from "@mui/x-data-grid-pro";
import { useContext, useEffect, useMemo, useState } from "react";
import CustomToolbar from "../../components/grid/CustomToolbar";
import GeneralContext from "../../context/GeneralContext/GeneralContext";
import api from "../../services/api";
import { diet_grid_columns_stage2 } from "./grid_columns/diet_grid_columns";
import { displayMessage } from "../../helpers/messages";
import SL_Button from "../../components/buttons/Button";
import { Modal } from "antd";
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusCircleOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { SL_Grid } from "../../components/grid/SL_Grid";
import { CreateDietModal } from "./CreateDietModal";
import { DetailDietModal } from "./DetailDietModal";
import { CombinationsStage } from "../../components/combinations/CombinationsStage";

// Mapeamentos para o rótulo da combinação no card
const GENDER_LABELS = { male: "Masc", female: "Fem" };
const TYPE_LABELS = { weight_loss: "Emagrecimento", muscle_gain: "Ganho de massa" };
const INTOLERANCE_LABELS = {
    none: "Sem intolerância",
    lactose: "Lactose",
    gluten: "Glúten",
    vegetarian: "Vegetariano",
};

// Constrói a chave + rótulo da combinação (gender + type + meals_count + food_intolerance)
const buildDietCombination = (item) => {
    const gender = GENDER_LABELS[item.gender] || item.gender || "?";
    const type = TYPE_LABELS[item.type] || item.type || "?";
    const meals = item.meals_count ? `${item.meals_count} refeições` : "?";
    const into = INTOLERANCE_LABELS[item.food_intolerance] || item.food_intolerance || "Sem intolerância";
    return {
        key: `${item.gender}|${item.type}|${item.meals_count}|${item.food_intolerance}`,
        label: `${gender} • ${type} • ${meals} • ${into}`,
    };
};

// Opções dos filtros por dimensão (Etapa 1), alinhadas ao modal de criação.
const GENDER_FILTERS = [
    { value: "male", label: "Masculino" },
    { value: "female", label: "Feminino" },
];
const TYPE_FILTERS = [
    { value: "weight_loss", label: displayMessage("WEIGHT_LOSS") },
    { value: "muscle_gain", label: displayMessage("MUSCLE_GAIN") },
];
const MEALS_FILTERS = [
    { value: 3, label: "3 refeições" },
    { value: 4, label: "4 refeições" },
    { value: 5, label: "5 refeições" },
    { value: 6, label: "6 refeições" },
];
const INTOLERANCE_FILTERS = [
    { value: "none", label: "Nenhuma" },
    { value: "lactose", label: "Lactose" },
    { value: "gluten", label: "Glúten" },
    { value: "vegetarian", label: "Vegetariano" },
];

const DIET_FILTER_CONFIGS = [
    { field: "gender", label: displayMessage("GENDER"), options: GENDER_FILTERS },
    { field: "type", label: displayMessage("DIET_TYPE"), options: TYPE_FILTERS },
    { field: "meals_count", label: displayMessage("MEALS_COUNT"), options: MEALS_FILTERS },
    { field: "food_intolerance", label: "Intolerância", options: INTOLERANCE_FILTERS },
];

// Extrai os campos da combinação a partir de um item representativo do grupo,
// para pré-preencher o modal de criação na Etapa 2.
const buildDietPreset = (item) => ({
    gender: item.gender,
    type: item.type,
    meals_count: item.meals_count,
    food_intolerance: item.food_intolerance,
});

export function ListDiets() {
	const [modal, contextHolder] = Modal.useModal();

	const [diets, setDiets] = useState(null);
	const [currentRow, setCurrentRow] = useState(null);
	const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
	const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);
	// Etapa 1: selectedCombination === null
	// Etapa 2: selectedCombination = grupo selecionado { key, label, items, seqs, missing }
	const [selectedCombination, setSelectedCombination] = useState(null);
	const [presetCombination, setPresetCombination] = useState(null);

	const { setShowLoading, mobile_mode } = useContext(GeneralContext);

	useEffect(() => {
		loadDiets();
	}, []);

	// Limpa a seleção de linha ao trocar de combinação para não acionar
	// ver/remover sobre uma dieta de outro grupo.
	useEffect(() => {
		setCurrentRow(null);
	}, [selectedCombination]);

	function handleRowChange(e) {
		setCurrentRow(e?.row);
	}

	// Criação "livre" (Etapa 1) — sem preset, modal completo.
	function onClickCreate() {
		setPresetCombination(null);
		setIsModalCreateOpen(true);
	}

	// Criação "dentro da combinação" (Etapa 2) — preset travado.
	function onClickCreateInCombination() {
		const first = stage2Rows[0];
		setPresetCombination(first ? buildDietPreset(first) : null);
		setIsModalCreateOpen(true);
	}

	async function onCloseCreateModal(loadData = false) {
		if (loadData) {
			await loadDiets();
		}
		setIsModalCreateOpen(false);
		setPresetCombination(null);
	}

	function onClickDetail() {
		setIsModalDetailOpen(true);
	}

	async function onCloseDetailModal(loadData = false) {
		if (loadData) {
			await loadDiets();
		}
		setIsModalDetailOpen(false);
	}

	async function loadDiets() {
		setShowLoading(true);
		try {
			const response = await api.diets.list();
			// A API retorna { total, diets, raw } - usamos o 'raw' que é o array
			setDiets(response.data.raw || []);
		} catch (error) {
			console.error("Erro ao carregar dietas:", error);
		} finally {
			setShowLoading(false);
		}
	}

	function onClickRemove() {
		modal.confirm({
			centered: true,
			content: (
				<div className="text-base w-full text-fit-red-500 text-center sm:text-start">
					{displayMessage("CONFIRM_REMOVE_DIET")}
				</div>
			),
			cancelText: displayMessage("CANCEL"),
			okText: displayMessage("CONFIRM"),
			okButtonProps: {
				danger: true,
			},
			onOk: removeDiet,
			icon: mobile_mode ? null : <WarningOutlined style={{ color: "red" }} />,
			cancelButtonProps: {
				style: { color: "gray !important" },
			},
		});
	}

	async function removeDiet() {
		try {
			setShowLoading(true);
			await api.diets.delete(currentRow.id);
			setCurrentRow(null);
			await loadDiets();
		} finally {
			setShowLoading(false);
		}
	}

	// Itens da combinação selecionada, derivados do estado `diets`
	// (sempre fresco após create/remove). Armazenamos apenas a chave + rótulo
	// da combinação em `selectedCombination`, e aqui filtramos/ordenamos em
	// memória — por isso a Etapa 2 atualiza sozinha ao recarregar a lista.
	const stage2Rows = useMemo(() => {
		if (!selectedCombination) return [];
		return (diets || [])
			.filter((d) => buildDietCombination(d).key === selectedCombination.key)
			.sort((a, b) => (a.sequence ?? 1) - (b.sequence ?? 1));
	}, [diets, selectedCombination]);

	return (
		<>
			{contextHolder}
			{isModalCreateOpen && (
				<CreateDietModal
					onCloseModal={onCloseCreateModal}
					presetCombination={presetCombination}
				/>
			)}
			{isModalDetailOpen && currentRow && (
				<DetailDietModal
					onCloseModal={onCloseDetailModal}
					dietId={currentRow.id}
				/>
			)}

			<div className="flex flex-col flex-1 h-full">
				<div className="flex flex-1 flex-col">
					<div className="flex flex-col flex-1 h-full">
						<div
							className={`flex w-full bg-blue-600 p-2 rounded-tl-[4px] rounded-tr-[4px] flex-wrap ${
								mobile_mode ? "flex-col items-center gap-4" : "gap-2"
							}`}
						>
							<div className="self-center text-white font-semibold text-base">
								{displayMessage("DIET_LIST_MANAGEMENT")}
							</div>

							<div className={`flex-1 flex flex-wrap gap-2 justify-end`}>
								<div
									className={`flex-1 flex ${
										mobile_mode ? "flex-col" : "flex-wrap"
									} gap-2 justify-end`}
								>
									{selectedCombination && (
										<SL_Button
											type="default"
											className={`flex items-center flex-1 w-full ss:max-w-[220px] ss:min-w-[160px]`}
											onClick={() => setSelectedCombination(null)}
											icon={
												<ArrowLeftOutlined className="text-base flex items-center" />
											}
										>
											<span className="flex-1">
												{displayMessage("COMBINATIONS_BACK")}
											</span>
										</SL_Button>
									)}

									{selectedCombination && (
										<SL_Button
											type="default"
											disabled={!currentRow}
											className={`flex items-center flex-1 w-full ss:max-w-[200px] ss:min-w-[200px]`}
											onClick={onClickDetail}
											icon={
												<EyeOutlined className="text-base flex items-center" />
											}
										>
											<span className="flex-1">
												{displayMessage("VIEW_DIET")}
											</span>
										</SL_Button>
									)}

									<SL_Button
										type="default"
										className={`flex items-center flex-1 w-full ss:max-w-[260px] ss:min-w-[200px]`}
										onClick={
											selectedCombination
												? onClickCreateInCombination
												: onClickCreate
										}
										icon={
											<PlusCircleOutlined className="text-base flex items-center" />
										}
									>
										<span className="flex-1">
											{selectedCombination
												? displayMessage("COMBINATIONS_ADD_IN_DIET")
												: displayMessage("ADD_DIET")}
										</span>
									</SL_Button>

									{selectedCombination && (
										<SL_Button
											disabled={!currentRow}
											className={`flex items-center flex-1 w-full ss:max-w-[200px] ss:min-w-[200px]`}
											danger={true}
											onClick={onClickRemove}
											icon={
												<DeleteOutlined className="text-base flex items-center" />
											}
										>
											<span className="flex-1">
												{displayMessage("REMOVE_DIET_ACTION")}
											</span>
										</SL_Button>
									)}
								</div>
							</div>
					</div>

					{/* Etapa 1 — Agrupamentos */}
					{!selectedCombination && (
						<CombinationsStage
							items={diets ?? []}
							buildCombination={buildDietCombination}
							filterConfigs={DIET_FILTER_CONFIGS}
							itemNamePlural={displayMessage("COMBINATIONS_ITEMS_DIET")}
							onSelect={(g) =>
								setSelectedCombination({ key: g.key, label: g.label })
							}
						/>
					)}

					{/* Etapa 2 — Itens da combinação, ordenados por sequência */}
					{selectedCombination && (
						<>
							<div className="px-2 py-1 bg-blue-50 border-b border-blue-100">
								<div className="text-blue-700 font-semibold text-sm">
									{displayMessage("COMBINATIONS_ITEMS_IN")}
								</div>
								<div className="text-gray-600 text-sm">
									{selectedCombination.label}
								</div>
							</div>

							<div className={`w-full flex-1 min-h-[400px] smb:min-h-0`}>
								<SL_Grid
									rows={stage2Rows}
									columns={diet_grid_columns_stage2}
									components={{
										Toolbar: () => <CustomToolbar />,
										Footer: () => <GridFooter />,
									}}
									onRowClick={handleRowChange}
								/>
							</div>
						</>
					)}
					</div>
				</div>
			</div>
		</>
	);
}
